import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Skeleton,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchData } from "../../api/client";
import FlowCanvas from "./flowBuilder/FlowCanvas";
import { migrateTriggersToGraph } from "./flowBuilder/graphModel";
import CampaignMetricsHeader from "./components/CampaignMetricsHeader";
import CampaignFunnelView from "./components/CampaignFunnelView";
import StageConfigPanel from "./components/StageConfigPanel";
import { useAuthContext } from "../../context/AuthContext";
import {
  can,
  scopeSetFromPermissions,
  useEffectivePermissions,
} from "../../hooks/useEffectivePermissions";
import { toFriendlyMessage } from "./campaignUi";

function safeJsonParse(s) {
  try {
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

function prettyJson(obj) {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return "{}";
  }
}

export default function CampaignDetailPage() {
  const { userData } = useAuthContext();
  const { data: permissionsData } = useEffectivePermissions();
  const permissionScopes = useMemo(
    () => scopeSetFromPermissions(permissionsData, userData),
    [permissionsData, userData]
  );
  const canUpdateCampaigns = can(permissionScopes, "campaigns", "update");
  const navigate = useNavigate();
  const params = useParams();
  const campaignId = params?.id ? Number(params.id) : null;

  const [campaign, setCampaign] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [flows, setFlows] = useState([]);

  const [advancedMode, setAdvancedMode] = useState(false);
  const [editorMode, setEditorMode] = useState("visual"); // visual | json
  const [flowConfigDraft, setFlowConfigDraft] = useState("");

  const [stagePanelOpen, setStagePanelOpen] = useState(false);
  const [activeStageId, setActiveStageId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const selectedFlowId = campaign?.flowId ?? null;
  const selectedFlow = useMemo(() => flows.find((f) => f.id === selectedFlowId) || null, [flows, selectedFlowId]);

  const refreshCampaign = async () => {
    if (!campaignId) return;
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await fetchData(`/campaigns/${campaignId}`, "GET");
      setCampaign(res?.data || null);
    } catch (err) {
      console.error("Erro ao carregar campanha:", err);
      setErrorMsg(toFriendlyMessage(err));
      setCampaign(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshFlows = async () => {
    try {
      const flowRes = await fetchData("/flows", "GET");
      setFlows(flowRes?.data || []);
    } catch (err) {
      console.error("Erro ao carregar flows:", err);
    }
  };

  const refreshMetrics = async () => {
    if (!campaignId) return;
    try {
      const res = await fetchData(`/campaigns/${campaignId}/metrics`, "GET");
      setMetrics(res?.data || null);
    } catch (err) {
      console.error("Erro ao carregar métricas:", err);
      setMetrics(null);
    }
  };

  useEffect(() => {
    refreshCampaign();
    refreshFlows();
    refreshMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  useEffect(() => {
    if (!selectedFlow) return;
    setFlowConfigDraft(prettyJson(selectedFlow.config || {}));
  }, [selectedFlow]);

  const selectedFlowConfigObj = useMemo(() => safeJsonParse(flowConfigDraft) || null, [flowConfigDraft]);
  const hasGraph = !!(selectedFlowConfigObj && selectedFlowConfigObj.graph);
  const hasMvpTriggerActions = useMemo(() => {
    const cfg = selectedFlowConfigObj && typeof selectedFlowConfigObj === "object" ? selectedFlowConfigObj : {};
    const t0 = Array.isArray(cfg.triggers) ? cfg.triggers[0] || {} : {};
    return !!(t0?.createLead || t0?.tag || t0?.dmText);
  }, [selectedFlowConfigObj]);

  const saveFlowConfig = async () => {
    const parsed = safeJsonParse(flowConfigDraft);
    if (!parsed) {
      setErrorMsg("A configuração do funil está inválida. Verifique o JSON e tente novamente.");
      return;
    }
    if (!selectedFlow) return;
    setErrorMsg("");
    try {
      await fetchData(`/flows/${selectedFlow.id}`, "PUT", { config: parsed });
      await refreshFlows();
      await refreshMetrics();
    } catch (err) {
      console.error("Erro ao salvar config:", err);
      setErrorMsg(toFriendlyMessage(err));
    }
  };

  const updateUxFunnelStage = (stageId, patch) => {
    const cfg = safeJsonParse(flowConfigDraft) || {};
    const ux = cfg.uxFunnel && typeof cfg.uxFunnel === "object" ? cfg.uxFunnel : {};
    const stages = ux.stages && typeof ux.stages === "object" ? ux.stages : {};
    const prev = stages[stageId] && typeof stages[stageId] === "object" ? stages[stageId] : {};
    const next = {
      ...cfg,
      uxFunnel: {
        ...ux,
        stages: {
          ...stages,
          [stageId]: { ...prev, ...patch },
        },
      },
    };
    setFlowConfigDraft(prettyJson(next));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 4, md: 5 }, maxWidth: 1400, mx: "auto" }}>
      {errorMsg ? (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      ) : null}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        justifyContent="space-between"
        spacing={2.5}
        mb={3}
      >
        <Box sx={{ minWidth: 0 }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/campaigns")}
            sx={{ mb: 1 }}
          >
            Voltar às campanhas
          </Button>
          {loading && !campaign ? (
            <Skeleton width={260} height={40} />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 700 }} noWrap title={campaign?.name || ""}>
              {campaign?.name || "Campanha"}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.55, maxWidth: 520 }}>
            Veja os resultados e configure o que acontece em cada etapa da jornada do cliente.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            onClick={() => {
              refreshCampaign();
              refreshFlows();
              refreshMetrics();
            }}
            disabled={loading}
          >
            Atualizar
          </Button>
          {canUpdateCampaigns ? (
            <FormControlLabel
              control={
                <Switch
                  checked={advancedMode}
                  onChange={(e) => setAdvancedMode(e.target.checked)}
                />
              }
              label="Modo técnico"
            />
          ) : null}
        </Stack>
      </Stack>

      <CampaignMetricsHeader campaign={campaign} metrics={metrics} />

      {!campaign && loading ? (
        <Skeleton variant="rounded" height={280} sx={{ borderRadius: 1 }} />
      ) : !campaign ? (
        <Alert severity="info">Campanha não encontrada.</Alert>
      ) : (
        <>
          {!advancedMode ? (
            <CampaignFunnelView
              campaign={campaign}
              metrics={metrics}
              flowConfig={selectedFlowConfigObj}
              onOpenStage={
                canUpdateCampaigns
                  ? (stageId) => {
                      setActiveStageId(stageId);
                      setStagePanelOpen(true);
                    }
                  : undefined
              }
            />
          ) : (
            <Card>
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ sm: "center" }}
                  justifyContent="space-between"
                  mb={1}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Editor técnico
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Para quem precisa editar o fluxo completo. Status: {campaign.status || "—"}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
                    <ToggleButtonGroup
                      size="small"
                      value={editorMode}
                      exclusive
                      onChange={(_, v) => (v ? setEditorMode(v) : null)}
                    >
                      <ToggleButton value="visual">Visual</ToggleButton>
                      <ToggleButton value="json">JSON</ToggleButton>
                    </ToggleButtonGroup>
                    {editorMode === "visual" && hasMvpTriggerActions ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const parsed = selectedFlowConfigObj;
                          if (!parsed) {
                            setErrorMsg(
                              "A configuração do funil está inválida. Corrija antes de converter."
                            );
                            return;
                          }
                          const next = migrateTriggersToGraph(parsed);
                          setFlowConfigDraft(prettyJson(next));
                        }}
                      >
                        Converter para grafo
                      </Button>
                    ) : null}
                    <Button size="small" variant="contained" onClick={saveFlowConfig} disabled={!selectedFlow}>
                      Salvar
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setFlowConfigDraft(prettyJson(selectedFlow?.config || {}))}
                      disabled={!selectedFlow}
                    >
                      Desfazer
                    </Button>
                  </Stack>
                </Stack>

                {!selectedFlow ? (
                  <Alert severity="info">Nenhum flow associado ainda (ou não carregado).</Alert>
                ) : editorMode === "visual" ? (
                  <>
                    {!selectedFlowConfigObj ? (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        O JSON atual está inválido. Troque para o modo JSON e corrija para usar o builder visual.
                      </Alert>
                    ) : null}
                    {hasGraph && hasMvpTriggerActions ? (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        Atenção: este flow tem <strong>graph</strong> e também ações no <strong>trigger</strong>. Isso pode duplicar ações.
                      </Alert>
                    ) : null}
                    {selectedFlowConfigObj ? (
                      <FlowCanvas config={selectedFlowConfigObj} onChangeConfig={(nextCfg) => setFlowConfigDraft(prettyJson(nextCfg))} />
                    ) : null}
                  </>
                ) : (
                  <Box>
                    <textarea
                      value={flowConfigDraft}
                      onChange={(e) => setFlowConfigDraft(e.target.value)}
                      style={{
                        width: "100%",
                        minHeight: 320,
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                        fontSize: 12,
                        padding: 12,
                        borderRadius: 8,
                        border: "1px solid rgba(0,0,0,0.12)",
                      }}
                      spellCheck={false}
                    />
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  No modo simples, as anotações de cada etapa ficam só para organização da equipe e não
                  alteram o envio automático. O que dispara mensagem de verdade está neste editor.
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <StageConfigPanel
        open={canUpdateCampaigns && stagePanelOpen}
        stageId={activeStageId}
        flowConfig={selectedFlowConfigObj}
        onClose={() => setStagePanelOpen(false)}
        onChange={(patch) => {
          if (!activeStageId) return;
          updateUxFunnelStage(activeStageId, patch);
        }}
        onSave={saveFlowConfig}
      />
    </Box>
  );
}

